/**
 * §7.6 + Phase 2.D — diamond speed-up cost formula.
 *
 * Pure-TS, server-authoritative. The same shape resolves cost for both
 * production jobs (§6.2) and construction jobs (§5.1 / §6.4.3); the client
 * uses it only to render a live preview, the server uses it as the source
 * of truth that lands on the wallet decrement.
 *
 * Locked formula (Vadim, 2026-05-03 monetisation lock — see CLAUDE.md):
 *   cost = max(1, ceil(remainingMs / 60_000))      remainingMs > 0
 *   cost = 0                                       remainingMs <= 0
 *
 * The zero-remaining case never reaches the wallet — the server returns
 * 409 ALREADY_COMPLETE before charging — but the helper still answers it
 * so client previews degrade gracefully across the deadline boundary.
 */

const MS_PER_MINUTE = 60_000;

/**
 * Diamond cost for a Speed-Up that finishes the remaining duration of a job.
 *
 * @param remainingMs  Milliseconds left until the job hits its deadline.
 *                     Negative or zero means "already done"; cost is 0.
 *                     Anything > 0 rounds up to the next whole minute and
 *                     is clamped to a minimum of 1 diamond per spec.
 *
 * @throws RangeError when `remainingMs` is non-finite (NaN, Infinity).
 */
export function speedUpDiamondCost(remainingMs: number): number {
  if (!Number.isFinite(remainingMs)) {
    throw new RangeError(`remainingMs must be finite, got ${remainingMs}`);
  }
  if (remainingMs <= 0) return 0;
  return Math.max(1, Math.ceil(remainingMs / MS_PER_MINUTE));
}

/**
 * Wall-clock remaining for a job whose deadline is `startedAtMs + durationMs`.
 * Floors at 0 so callers don't accidentally feed a negative back into the
 * cost formula or the progress bar.
 *
 * @throws RangeError when any of the three inputs is non-finite.
 */
export function jobRemainingMs(
  startedAtMs: number,
  durationMs: number,
  nowMs: number,
): number {
  if (!Number.isFinite(startedAtMs)) {
    throw new RangeError(`startedAtMs must be finite, got ${startedAtMs}`);
  }
  if (!Number.isFinite(durationMs)) {
    throw new RangeError(`durationMs must be finite, got ${durationMs}`);
  }
  if (!Number.isFinite(nowMs)) {
    throw new RangeError(`nowMs must be finite, got ${nowMs}`);
  }
  return Math.max(0, startedAtMs + durationMs - nowMs);
}
